import type { RangeValue } from '@react-types/shared';
import type { CalendarDate } from '@internationalized/date';
import { Description, Label } from '@heroui/react';
import {
  calendarDateToDateOnly,
  dateOnlyRangeToCalendarRange,
  dateOnlyToCalendarDate,
  formatDateOnlyBR,
  getTodayDateOnly,
} from '../../utils/date-utils';
import { HeroUiRangeCalendarGrid } from './hero-ui-range-calendar-grid';

interface InlineDateRangeSectionProps {
  title: string;
  startDate: string;
  endDate: string;
  onChange: (startDate: string, endDate: string) => void;
  /** Data mínima selecionável. Passe `null` para permitir qualquer data. */
  minDate?: string | null;
}

function normalizeRange(range: RangeValue<CalendarDate>): { start: CalendarDate; end: CalendarDate } {
  const start = range.start;
  const end = range.end ?? range.start;
  if (!start || !end) {
    throw new Error('Intervalo de datas inválido.');
  }
  if (end.compare(start) < 0) {
    return { start: end, end: start };
  }
  return { start, end };
}

export function InlineDateRangeSection({
  title,
  startDate,
  endDate,
  onChange,
  minDate = getTodayDateOnly(),
}: InlineDateRangeSectionProps) {
  const hasRange = Boolean(startDate && endDate);
  const rangeValue = hasRange ? dateOnlyRangeToCalendarRange(startDate, endDate) : null;
  const summary = hasRange
    ? `${formatDateOnlyBR(startDate)} — ${formatDateOnlyBR(endDate)}`
    : 'Selecione o início e o fim no calendário.';

  const handleRangeChange = (range: RangeValue<CalendarDate>) => {
    if (!range?.start) return;
    const normalized = normalizeRange(range);
    onChange(
      calendarDateToDateOnly(normalized.start),
      calendarDateToDateOnly(normalized.end),
    );
  };

  return (
    <section className="flex flex-col gap-3">
      <div className="flex flex-col gap-1">
        <Label className="text-base font-semibold">{title}</Label>
        <Description>{summary}</Description>
      </div>
      <div className="flex justify-center">
        <HeroUiRangeCalendarGrid
          ariaLabel={title}
          value={rangeValue}
          onChange={handleRangeChange}
          minValue={minDate ? dateOnlyToCalendarDate(minDate) : undefined}
        />
      </div>
    </section>
  );
}
